/**
 * TBGenSurv Module 5 — Geospatial & Phylogenetic Data
 *
 * Zimbabwe district-level TB surveillance data.
 * Coordinates based on real Zimbabwe administrative boundaries.
 * TB burden data modelled on WHO Zimbabwe TB reports (2023-2025).
 *
 * Spatial analysis methods:
 *   Hotspot detection:  Getis-Ord Gi* statistic
 *   Cluster detection:  DBSCAN spatial clustering
 *   Transmission links: SNP distance threshold ≤ 12 SNPs
 *   Phylogenetics:      Maximum likelihood (IQ-TREE2)
 */

// ── ZIMBABWE PROVINCES & DISTRICTS ───────────────────────────────────
export const PROVINCES = [
  {
    id: 'HAR', name: 'Harare',              lat: -17.829, lng: 31.052,
    tb_cases: 4821, mdr_cases: 312, population: 2123132,
    tb_rate: 227, hotspot: true, alert: 'critical',
    color: '#E74C3C',
    districts: [
      { name: 'Harare Urban',   lat: -17.829, lng: 31.052, cases: 2841, mdr: 187, rate: 312 },
      { name: 'Chitungwiza',    lat: -18.013, lng: 31.074, cases: 1124, mdr: 78,  rate: 287 },
      { name: 'Epworth',        lat: -17.882, lng: 31.172, cases: 856,  mdr: 47,  rate: 341 },
    ],
  },
  {
    id: 'BUL', name: 'Bulawayo',            lat: -20.150, lng: 28.585,
    tb_cases: 2914, mdr_cases: 198, population: 653337,
    tb_rate: 446, hotspot: true, alert: 'high',
    color: '#E67E22',
    districts: [
      { name: 'Bulawayo Urban', lat: -20.150, lng: 28.585, cases: 2914, mdr: 198, rate: 446 },
    ],
  },
  {
    id: 'MAN', name: 'Manicaland',          lat: -18.975, lng: 32.650,
    tb_cases: 1876, mdr_cases: 87,  population: 1752698,
    tb_rate: 107, hotspot: false, alert: 'moderate',
    color: '#F39C12',
    districts: [
      { name: 'Mutare',         lat: -18.975, lng: 32.650, cases: 734, mdr: 42,  rate: 187 },
      { name: 'Chipinge',       lat: -20.200, lng: 32.617, cases: 412, mdr: 18,  rate: 134 },
      { name: 'Rusape',         lat: -18.533, lng: 32.133, cases: 287, mdr: 14,  rate: 98  },
      { name: 'Makoni',         lat: -18.317, lng: 32.033, cases: 443, mdr: 13,  rate: 76  },
    ],
  },
  {
    id: 'MSC', name: 'Mashonaland Central', lat: -17.300, lng: 31.317,
    tb_cases: 987,  mdr_cases: 34,  population: 1271305,
    tb_rate: 78,  hotspot: false, alert: 'low',
    color: '#27AE60',
    districts: [
      { name: 'Bindura',        lat: -17.300, lng: 31.317, cases: 312, mdr: 12,  rate: 89  },
      { name: 'Mount Darwin',   lat: -16.783, lng: 31.583, cases: 287, mdr: 9,   rate: 71  },
      { name: 'Mazowe',         lat: -17.517, lng: 30.967, cases: 388, mdr: 13,  rate: 74  },
    ],
  },
  {
    id: 'MSE', name: 'Mashonaland East',    lat: -18.183, lng: 31.550,
    tb_cases: 1124, mdr_cases: 56,  population: 1257060,
    tb_rate: 89,  hotspot: false, alert: 'moderate',
    color: '#3498DB',
    districts: [
      { name: 'Marondera',      lat: -18.183, lng: 31.550, cases: 412, mdr: 21,  rate: 98  },
      { name: 'Mutoko',         lat: -17.400, lng: 32.217, cases: 287, mdr: 14,  rate: 81  },
      { name: 'Mudzi',          lat: -16.400, lng: 32.683, cases: 425, mdr: 21,  rate: 84  },
    ],
  },
  {
    id: 'MSW', name: 'Mashonaland West',    lat: -17.483, lng: 29.817,
    tb_cases: 876,  mdr_cases: 31,  population: 1501677,
    tb_rate: 58,  hotspot: false, alert: 'low',
    color: '#9B59B6',
    districts: [
      { name: 'Chinhoyi',       lat: -17.367, lng: 30.200, cases: 312, mdr: 12,  rate: 67  },
      { name: 'Kariba',         lat: -16.517, lng: 28.800, cases: 187, mdr: 7,   rate: 54  },
      { name: 'Hurungwe',       lat: -16.417, lng: 30.100, cases: 377, mdr: 12,  rate: 52  },
    ],
  },
  {
    id: 'MVG', name: 'Masvingo',            lat: -20.067, lng: 30.833,
    tb_cases: 1243, mdr_cases: 61,  population: 1485488,
    tb_rate: 84,  hotspot: false, alert: 'moderate',
    color: '#1ABC9C',
    districts: [
      { name: 'Masvingo Urban', lat: -20.067, lng: 30.833, cases: 487, mdr: 28,  rate: 127 },
      { name: 'Chiredzi',       lat: -21.050, lng: 31.667, cases: 387, mdr: 18,  rate: 78  },
      { name: 'Mwenezi',        lat: -21.067, lng: 30.567, cases: 369, mdr: 15,  rate: 67  },
    ],
  },
  {
    id: 'MBN', name: 'Matabeleland North',  lat: -18.533, lng: 27.017,
    tb_cases: 743,  mdr_cases: 28,  population: 749017,
    tb_rate: 99,  hotspot: false, alert: 'moderate',
    color: '#E91E63',
    districts: [
      { name: 'Hwange',         lat: -18.367, lng: 26.500, cases: 287, mdr: 11,  rate: 112 },
      { name: 'Binga',          lat: -17.617, lng: 27.350, cases: 198, mdr: 8,   rate: 87  },
      { name: 'Lupane',         lat: -18.933, lng: 27.800, cases: 258, mdr: 9,   rate: 94  },
    ],
  },
  {
    id: 'MBS', name: 'Matabeleland South',  lat: -20.583, lng: 29.083,
    tb_cases: 612,  mdr_cases: 19,  population: 683893,
    tb_rate: 89,  hotspot: false, alert: 'low',
    color: '#00BCD4',
    districts: [
      { name: 'Gwanda',         lat: -20.933, lng: 29.000, cases: 287, mdr: 9,   rate: 98  },
      { name: 'Beit Bridge',    lat: -22.217, lng: 30.000, cases: 198, mdr: 6,   rate: 84  },
      { name: 'Insiza',         lat: -20.167, lng: 29.283, cases: 127, mdr: 4,   rate: 71  },
    ],
  },
  {
    id: 'MID', name: 'Midlands',            lat: -19.450, lng: 29.817,
    tb_cases: 1587, mdr_cases: 72,  population: 1614941,
    tb_rate: 98,  hotspot: false, alert: 'moderate',
    color: '#FF9800',
    districts: [
      { name: 'Gweru',          lat: -19.450, lng: 29.817, cases: 587, mdr: 31,  rate: 124 },
      { name: 'Kwekwe',         lat: -18.917, lng: 29.817, cases: 412, mdr: 19,  rate: 107 },
      { name: 'Shurugwi',       lat: -19.667, lng: 30.017, cases: 287, mdr: 12,  rate: 89  },
      { name: 'Zvishavane',     lat: -20.333, lng: 30.033, cases: 301, mdr: 10,  rate: 84  },
    ],
  },
];

// ── TRANSMISSION CLUSTERS (WGS-confirmed) ─────────────────────────────
export const CLUSTERS = [
  {
    id: 'CLU-001',
    name: 'Harare Urban Cluster A',
    size: 14,
    classification: 'Pre-XDR-TB',
    lineage: 'Lineage 4.1.2.1',
    first_detected: '2025-11-12',
    last_detected:  '2026-03-14',
    status: 'Active',
    alert_level: 'critical',
    location: { lat: -17.855, lng: 31.034, province: 'Harare', district: 'Harare Urban' },
    snp_distance: 4.2,
    isolates: [
      { id: 'TBS-HAR-001', sample_code: 'TBS-HAR-20251112-0001', lat: -17.862, lng: 31.041, date: '2025-11-12', classification: 'Pre-XDR-TB', index_case: true  },
      { id: 'TBS-HAR-002', sample_code: 'TBS-HAR-20251118-0004', lat: -17.849, lng: 31.028, date: '2025-11-18', classification: 'Pre-XDR-TB', index_case: false },
      { id: 'TBS-HAR-003', sample_code: 'TBS-HAR-20251203-0009', lat: -17.871, lng: 31.039, date: '2025-12-03', classification: 'Pre-XDR-TB', index_case: false },
      { id: 'TBS-HAR-004', sample_code: 'TBS-HAR-20260115-0018', lat: -17.844, lng: 31.052, date: '2026-01-15', classification: 'MDR-TB',     index_case: false },
      { id: 'TBS-HAR-005', sample_code: 'TBS-HAR-20260310-0039', lat: -17.882, lng: 31.172, date: '2026-03-10', classification: 'Pre-XDR-TB', index_case: false },
    ],
    transmission_links: [
      { from: 'TBS-HAR-001', to: 'TBS-HAR-002', snp_dist: 3, confidence: 'High'   },
      { from: 'TBS-HAR-001', to: 'TBS-HAR-003', snp_dist: 5, confidence: 'High'   },
      { from: 'TBS-HAR-002', to: 'TBS-HAR-004', snp_dist: 6, confidence: 'High'   },
      { from: 'TBS-HAR-003', to: 'TBS-HAR-005', snp_dist: 4, confidence: 'High'   },
    ],
    key_mutations: ['rpoB S450L', 'katG S315T', 'gyrA D94G', 'inhA c-15t'],
    notes: 'Active transmission cluster linked to Epworth Clinic catchment area. Index case identified November 2025. 4 secondary cases confirmed by WGS. MoHCC notified.',
  },
  {
    id: 'CLU-002',
    name: 'Bulawayo MDR Cluster',
    size: 8,
    classification: 'MDR-TB',
    lineage: 'Lineage 2.2.1 (Beijing)',
    first_detected: '2025-12-01',
    last_detected:  '2026-02-28',
    status: 'Active',
    alert_level: 'high',
    location: { lat: -20.155, lng: 28.591, province: 'Bulawayo', district: 'Bulawayo Urban' },
    snp_distance: 5.8,
    isolates: [
      { id: 'TBS-BUL-001', sample_code: 'TBS-BUL-20251201-0003', lat: -20.148, lng: 28.579, date: '2025-12-01', classification: 'MDR-TB', index_case: true  },
      { id: 'TBS-BUL-002', sample_code: 'TBS-BUL-20251215-0007', lat: -20.162, lng: 28.594, date: '2025-12-15', classification: 'MDR-TB', index_case: false },
      { id: 'TBS-BUL-003', sample_code: 'TBS-BUL-20260118-0011', lat: -20.171, lng: 28.582, date: '2026-01-18', classification: 'MDR-TB', index_case: false },
    ],
    transmission_links: [
      { from: 'TBS-BUL-001', to: 'TBS-BUL-002', snp_dist: 4, confidence: 'High' },
      { from: 'TBS-BUL-001', to: 'TBS-BUL-003', snp_dist: 7, confidence: 'Mod'  },
    ],
    key_mutations: ['rpoB S450L', 'katG S315T', 'inhA c-15t'],
    notes: 'Beijing lineage cluster unusual for southern Africa. Mining community exposure suspected. Contact tracing ongoing.',
  },
  {
    id: 'CLU-003',
    name: 'Mutare DS-TB Cluster',
    size: 5,
    classification: 'DS-TB',
    lineage: 'Lineage 4.3.2',
    first_detected: '2026-01-08',
    last_detected:  '2026-02-14',
    status: 'Resolved',
    alert_level: 'moderate',
    location: { lat: -18.980, lng: 32.655, province: 'Manicaland', district: 'Mutare' },
    snp_distance: 6.1,
    isolates: [
      { id: 'TBS-MAN-001', sample_code: 'TBS-MAN-20260108-0002', lat: -18.975, lng: 32.650, date: '2026-01-08', classification: 'DS-TB', index_case: true  },
      { id: 'TBS-MAN-002', sample_code: 'TBS-MAN-20260122-0006', lat: -18.988, lng: 32.661, date: '2026-01-22', classification: 'DS-TB', index_case: false },
    ],
    transmission_links: [
      { from: 'TBS-MAN-001', to: 'TBS-MAN-002', snp_dist: 5, confidence: 'High' },
    ],
    key_mutations: [],
    notes: 'Drug-susceptible cluster. School setting. All contacts traced and treated. Resolved.',
  },
];

// ── PHYLOGENETIC TREE DATA ────────────────────────────────────────────
export const PHYLO_TREE = {
  name: 'ZW-2026-WGS-Cohort',
  totalLeaves: 47,
  nodes: [
    {
      id: 'root', label: 'Root (H37Rv)', depth: 0, x: 50, y: 50,
      children: [
        {
          id: 'L4', label: 'Lineage 4 (Euro-American)', depth: 1, x: 30, y: 30, lineage: 'L4',
          children: [
            {
              id: 'CLU1', label: 'CLU-001 (Harare Pre-XDR)', depth: 2, x: 15, y: 20, cluster: 'CLU-001',
              children: [
                { id: 'HAR001', label: 'TBS-HAR-001 ★', depth: 3, x: 8,  y: 10, cluster: 'CLU-001', index: true,  classification: 'Pre-XDR-TB' },
                { id: 'HAR002', label: 'TBS-HAR-002',   depth: 3, x: 8,  y: 18, cluster: 'CLU-001', index: false, classification: 'Pre-XDR-TB' },
                { id: 'HAR003', label: 'TBS-HAR-003',   depth: 3, x: 8,  y: 26, cluster: 'CLU-001', index: false, classification: 'Pre-XDR-TB' },
                { id: 'HAR004', label: 'TBS-HAR-004',   depth: 3, x: 8,  y: 34, cluster: 'CLU-001', index: false, classification: 'MDR-TB'     },
                { id: 'HAR005', label: 'TBS-HAR-005',   depth: 3, x: 8,  y: 42, cluster: 'CLU-001', index: false, classification: 'Pre-XDR-TB' },
              ],
            },
            {
              id: 'MAN_clu', label: 'CLU-003 (Mutare DS)', depth: 2, x: 15, y: 55, cluster: 'CLU-003',
              children: [
                { id: 'MAN001', label: 'TBS-MAN-001 ★', depth: 3, x: 8, y: 50, cluster: 'CLU-003', index: true,  classification: 'DS-TB' },
                { id: 'MAN002', label: 'TBS-MAN-002',   depth: 3, x: 8, y: 58, cluster: 'CLU-003', index: false, classification: 'DS-TB' },
              ],
            },
          ],
        },
        {
          id: 'L2', label: 'Lineage 2 (Beijing)', depth: 1, x: 30, y: 75, lineage: 'L2',
          children: [
            {
              id: 'CLU2', label: 'CLU-002 (Bulawayo MDR)', depth: 2, x: 15, y: 75, cluster: 'CLU-002',
              children: [
                { id: 'BUL001', label: 'TBS-BUL-001 ★', depth: 3, x: 8, y: 70, cluster: 'CLU-002', index: true,  classification: 'MDR-TB' },
                { id: 'BUL002', label: 'TBS-BUL-002',   depth: 3, x: 8, y: 78, cluster: 'CLU-002', index: false, classification: 'MDR-TB' },
                { id: 'BUL003', label: 'TBS-BUL-003',   depth: 3, x: 8, y: 86, cluster: 'CLU-002', index: false, classification: 'MDR-TB' },
              ],
            },
          ],
        },
      ],
    },
  ],
};

// ── SNP DISTANCE MATRIX ───────────────────────────────────────────────
export const SNP_MATRIX = {
  samples: ['HAR001','HAR002','HAR003','HAR004','HAR005','BUL001','BUL002','MAN001'],
  distances: [
    [0,  3,  5,  9,  4,  87, 91, 102],
    [3,  0,  6,  6,  7,  89, 88, 104],
    [5,  6,  0,  11, 4,  91, 93, 98 ],
    [9,  6,  11, 0,  12, 86, 84, 107],
    [4,  7,  4,  12, 0,  92, 95, 101],
    [87, 89, 91, 86, 92, 0,  4,  118 ],
    [91, 88, 93, 84, 95, 4,  0,  121 ],
    [102,104,98, 107,101,118,121,0   ],
  ],
};

// ── HOTSPOT ANALYSIS ──────────────────────────────────────────────────
export const HOTSPOTS = [
  { province: 'Harare',    gi_star: 3.82, pvalue: 0.0001, status: 'Hot spot 99% CI',   color: '#C0392B' },
  { province: 'Bulawayo',  gi_star: 2.94, pvalue: 0.003,  status: 'Hot spot 95% CI',   color: '#E74C3C' },
  { province: 'Midlands',  gi_star: 1.21, pvalue: 0.112,  status: 'Not significant',   color: '#95A5A6' },
  { province: 'Masvingo',  gi_star: 0.87, pvalue: 0.234,  status: 'Not significant',   color: '#95A5A6' },
  { province: 'Manicaland',gi_star: 1.64, pvalue: 0.051,  status: 'Not significant',   color: '#F39C12' },
  { province: 'Mash East', gi_star: 0.42, pvalue: 0.412,  status: 'Not significant',   color: '#95A5A6' },
  { province: 'Mash West', gi_star:-1.12, pvalue: 0.134,  status: 'Cold spot (border)',color: '#3498DB' },
  { province: 'Mat North', gi_star: 0.91, pvalue: 0.198,  status: 'Not significant',   color: '#95A5A6' },
];

// ── SOCIODEMOGRAPHIC OVERLAY ──────────────────────────────────────────
export const SOCIODEM = {
  HAR: { hiv_prev: 12.4, poverty_pct: 34, healthcare_access: 87, population_density: 3421 },
  BUL: { hiv_prev: 14.1, poverty_pct: 41, healthcare_access: 79, population_density: 1247 },
  MAN: { hiv_prev: 16.8, poverty_pct: 58, healthcare_access: 54, population_density: 42  },
  MSC: { hiv_prev: 11.2, poverty_pct: 52, healthcare_access: 48, population_density: 31  },
  MSE: { hiv_prev: 13.4, poverty_pct: 49, healthcare_access: 51, population_density: 38  },
  MSW: { hiv_prev: 10.8, poverty_pct: 44, healthcare_access: 56, population_density: 28  },
  MVG: { hiv_prev: 15.2, poverty_pct: 61, healthcare_access: 43, population_density: 34  },
  MBN: { hiv_prev: 13.7, poverty_pct: 67, healthcare_access: 38, population_density: 12  },
  MBS: { hiv_prev: 12.9, poverty_pct: 63, healthcare_access: 41, population_density: 10  },
  MID: { hiv_prev: 11.8, poverty_pct: 46, healthcare_access: 61, population_density: 47  },
};

// ── TREND DATA ────────────────────────────────────────────────────────
export const TREND_DATA = [
  { month: 'Jan 25', total: 1187, mdr: 78,  xdr: 8  },
  { month: 'Feb 25', total: 1203, mdr: 81,  xdr: 9  },
  { month: 'Mar 25', total: 1241, mdr: 84,  xdr: 11 },
  { month: 'Apr 25', total: 1198, mdr: 79,  xdr: 10 },
  { month: 'May 25', total: 1312, mdr: 91,  xdr: 13 },
  { month: 'Jun 25', total: 1287, mdr: 88,  xdr: 12 },
  { month: 'Jul 25', total: 1341, mdr: 94,  xdr: 14 },
  { month: 'Aug 25', total: 1298, mdr: 89,  xdr: 13 },
  { month: 'Sep 25', total: 1387, mdr: 98,  xdr: 16 },
  { month: 'Oct 25', total: 1421, mdr: 103, xdr: 18 },
  { month: 'Nov 25', total: 1398, mdr: 101, xdr: 17 },
  { month: 'Dec 25', total: 1312, mdr: 96,  xdr: 15 },
  { month: 'Jan 26', total: 1456, mdr: 108, xdr: 19 },
  { month: 'Feb 26', total: 1487, mdr: 112, xdr: 21 },
  { month: 'Mar 26', total: 1521, mdr: 118, xdr: 22 },
];
